import {useEffect, useState} from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { useIntlWrapperContext } from '../../Helpers/IntlWrapperProvider';
import useFetchWrapper, {HttpResponse} from '../../Helpers/useFetchWrapper';

import {
	ApplicantFormData,
	RegistrationApplicantData,
	MenteeFormData,
	MentorFormData,
	PasswordStrength,
	RegistrationMenteeData,
	RegistrationMentorData, MeetingsResponse, Meeting
} from './Registration.model';
import {useIntl} from "react-intl";
import {WORDPRESS_DOMAIN} from "../../Helpers/EnvVars";

const useRegistration = () => {
	const [ activeStep, setActiveStep ] = useState(0);
	const [ passwordStrength, setPasswordStrength ] = useState(PasswordStrength.Weak);
	const [ userStepperFinished, setUserStepperFinished ] = useState(false);
	const [futureMeetings, setFutureMeetings] = useState<Meeting[]>([]);
	const [futureMeeting, setFutureMeeting] = useState<string | undefined>(undefined);
	const [previousMeetings, setPreviousMeetings] = useState<Meeting[]>([]);
	const intl = useIntl();
	
	useEffect(() => {
		getPreviousMeetings()
		getFutureMeetings();
	}, [])

	const { get, post } = useFetchWrapper();
	const { i18nCountries, i18nLanguages } = useIntlWrapperContext();
	const history = useHistory();

	const mapApplicantMenteeData = (data: ApplicantFormData): RegistrationApplicantData => {
		return {
			acceptedTac: data.acceptedTac,
			email: data.email,
			firstName: data.firstName,
			lastName: data.lastName,
			location: data.currentLocation,
			role: "Mentee",
		}
	}

	const mapMenteeData = (data: MenteeFormData): RegistrationMenteeData => {
		return {
      acceptedTac: data.acceptedTac,
      email: data.email,
      firstName: data.firstName,
      includeInNetwork: data.includeInNetwork,
      includeInSearch: data.includeInSearch,
      lastName: data.lastName,
      location: data.currentLocation,
      password: data.password,
      payload: {
        MenteeApplication: {}
      },
      previousLnlm: futureMeeting ? futureMeeting : data.previousLnlm,
      role: "Mentee"
    }
	}

	const mapMentorData = (data: MentorFormData): RegistrationMentorData => {
		return {
      acceptedTac: data.acceptedTac,
      email: data.email,
      firstName: data.firstName,
      includeInNetwork: data.includeInNetwork,
      includeInSearch: data.includeInSearch,
      lastName: data.lastName,
      location: data.currentLocation,
      password: data.password,
      payload: {
        MentorApplication: {
          acceptedCoc: data.acceptedCoc,
          currentPosition: data.currentPosition,
          institution: data.institution,
          languages: data.languages,
          location: data.location,
          mentoringScopes: data.mentoringScopes,
          motivation: data.motivation,
          researchAreas: data.researchAreas,
          startDate: moment(data.startDate).format('YYYY-MM-DD')
        }
      },
      previousLnlm: data.previousLnlm,
      role: "Mentor"
    }
	}

	const disabledDate = (current: any) => {
		return current.unix() > new Date().getTime() / 1000;
	}

	const onPasswordChange = (event: any) => {
		const { value } = event.currentTarget;

		if (/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})/.test(value)) {
			setPasswordStrength(PasswordStrength.Strong);
		}	else if (/(?=.*[a-z])(?=.*[0-9])(?=.{8,})/.test(value)) {
			setPasswordStrength(PasswordStrength.Medium);
		} else {
			setPasswordStrength(PasswordStrength.Weak);
		}
	}

	const navigateTo = (path: string) => {
		history.push(path);
	}

	const onSubmitMentorForm = async (data: MentorFormData) => {
		const res = await post(`/application`, mapMentorData(data), true);
		if (res.ok) {
			navigateTo('/registration/thank-you/mentor-application');
		}
		// TODO: error handling

	}
	
	const onSubmitGeneralInfoForm = async (data: MenteeFormData) => {
		const { acceptedTac, previousLnlm, role } = data;

		// If user choosed to be a mentee, submit data and finish registration flow
		if ((!previousLnlm && !futureMeeting) && !acceptedTac) {
			window.open(WORDPRESS_DOMAIN, "_self")
		} else if ((!previousLnlm && !futureMeeting) && acceptedTac) {
			const res = await post(`/interrupted-application`, mapApplicantMenteeData(data), true);

			if (res.ok) {
				window.open(WORDPRESS_DOMAIN, "_self")
			}
		} else {
			if (role === 'Mentee') {
				const res = await post(`/application`, mapMenteeData(data), true);

				if (res.ok) {
					if(futureMeeting) {
						navigateTo('/registration/thank-you/applicant-mentee-application');
						return;
					}
					navigateTo('/registration/thank-you/mentee-application');
				}
			} else {
				setActiveStep(0);
				setUserStepperFinished(true);
			}
		}
	}

	const validateEmailAddress = async (email: string) => {
		if (email) {
			const res = await get(`/application/validate-email?email=${email}`);
			return res.ok;
		}
		return false;
	};
	
	
	const getPreviousMeetings = async () => {
		const res: HttpResponse<MeetingsResponse> = await get(
			`/conference/all?page=${1}&size=${99999}&isFuture=false`
		);
		if(res.parsedBody) {
			setPreviousMeetings(res?.parsedBody?.conferences);
		}
	};
	
	const getFutureMeetings = async () => {
		const res: HttpResponse<MeetingsResponse> = await get(
			`/conference/all?page=${1}&size=${99999}&isFuture=true`
		);
		if(res.parsedBody) {
			setFutureMeetings(res?.parsedBody?.conferences);		
		}
	};
	
	const formatDate = (date: string | Date) => {
		return moment(date).format("DD MMM YYYY");
	};

	return {
    disabledDate,
		formatDate,
    navigateTo,
    onPasswordChange,
    onSubmitGeneralInfoForm,
    onSubmitMentorForm,
		setFutureMeeting,
    state: {
      activeStep,
			futureMeeting,
			futureMeetings,
      i18nCountries,
      i18nLanguages,
      passwordStrength,
			previousMeetings,
      setActiveStep,
      userStepperFinished
    },
    validateEmailAddress
  }
}

export default useRegistration;
